<template>
  <div :class="customClass" v-bind="$attrs">
    <base-button
      full-width
      :status="status"
      :x-large="!isBrowse"
      :secondary="isBrowse"
      :primary="!isBrowse"
      :active="isSubscribed"
      :disabled="buttonDisabled"
      @click="handleClick()"
    >
      <div
        class="tw-flex tw-items-center tw-justify-center tw-space-x-1 tw-w-48 tw-uppercase"
        :class="isBrowse ? 'xs:tw-text-h6 ' : 'xs:tw-text-xl'"
      >
        <span>{{ subscribeText }}</span>
      </div>
    </base-button>
  </div>

  <p
    v-if="!isBrowse && scarcity && stock && mod.price"
    class="tw-text-center tw-mt-1"
  >
    {{ $t('marketplace.available_stock', { count: humanNumberFormat(stock) }) }}
  </p>

  <teleport to="body">
    <component
      :is="gameUSDMarketplace ? 'ugc-usd-purchase-modal' : 'ugc-purchase-modal'"
      v-if="isPurchaseModalOpen"
      :game="game"
      :mod="mod"
      @close:purchasemodal="closePurchaseModal"
      @purchase:complete="handlePurchase"
    />
  </teleport>
</template>

<script>
import UgcUsdPurchaseModal from '@components/Modals/UgcUsdPurchaseModal.vue'
import { MODAL_DEPENDENCIES, MODAL_LOGIN } from '@config/globalModals.js'
import UgcPurchaseModal from '@components/Modals/UgcPurchaseModal.vue'
import { humanPriceFormat } from '@helpers/monetizationUtils.js'
import { setGlobalModal } from '@composables/useModal.js'
import * as sdkInterface from '@plugins/sdkInterface.js'
import { humanNumberFormat } from '@helpers/utils.js'
import { computed, toRefs, watch, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  isMarketplaceEnabledForMod,
  isTokenMarketplaceEnabled,
  isUSDMarketplaceEnabled,
  isMonetizationEnabled,
  isScarcityEnabled,
} from '@helpers/bitHelper.js'
import {
  useResourceValue,
  useMonetization,
  useStatus,
  useAsync,
  addToast,
  useModal,
  useRoute,
} from '@composables'
import {
  unsubscribeToModRequest,
  getDependenciesRequest,
  subscribeToModRequest,
} from '@services'
import {
  RESOURCE_ALREADY_SUBSCRIBED,
  RESOURCE_NOT_SUBSCRIBED,
} from '@shared/js/config/errors.js'
import {
  subscriptionStore,
  activationStore,
  purchasesStore,
  authStore,
} from '@stores'
export default {
  components: {
    UgcUsdPurchaseModal,
    UgcPurchaseModal,
  },
  props: {
    game: {
      type: Object,
      required: true,
    },
    mod: {
      type: Object,
      default: () => ({}),
    },
    isBrowse: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dependencies: {
      type: Array,
      default: null,
    },
    customClass: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const { mod, game, disabled, dependencies } = toRefs(props)
    const stock = ref(mod.value.stock)

    const { isLoggedIn, isMonRegistered } = authStore()
    const { addActivation } = activationStore()
    const { modOwned } = purchasesStore()
    const { subscriptions, removeSubscription, addSubscription } =
      subscriptionStore()

    const { loadingRegister, registerUser, purchaseMod } = useMonetization()
    const { tokenName } = useResourceValue()
    const { t } = useI18n()
    const { routeQuery } = useRoute()

    const {
      isModalOpen: isPurchaseModalOpen,
      hideModal: hidePurchaseModal,
      showModal: showPurchaseModal,
    } = useModal()

    const {
      run: subscribeToMod,
      error: subscribeError,
      errorRef: subErrorRef,
    } = useAsync(() =>
      subscribeToModRequest({
        gameId: game.value.id,
        modId: mod.value.id,
      })
    )

    const {
      run: unsubscribeToMod,
      error: unsubscribeError,
      errorRef: unsubErrorRef,
    } = useAsync(() =>
      unsubscribeToModRequest({
        gameId: game.value.id,
        modId: mod.value.id,
      })
    )

    const {
      run: runFetchDeps,
      error: errorDeps,
      data: dataDeps,
    } = useAsync(() =>
      getDependenciesRequest(game.value.name_id, mod.value.name_id)
    )

    const { status } = useStatus({ loading: loadingRegister })

    // use game and mod id
    const isSubscribed = computed(
      () =>
        !!subscriptions.value?.length &&
        !!mod.value?.id &&
        subscriptions.value.some(
          (subscription) =>
            subscription?.id === parseInt(mod.value.id) &&
            subscription.game_id === parseInt(game.value.id)
        )
    )

    const isPurchased = computed(() => modOwned(mod.value.id))

    const paymentRequired = computed(
      () => !!mod.value.price && !isPurchased.value
    )

    const gameUSDMarketplace = computed(() =>
      isUSDMarketplaceEnabled(game.value.monetization_options)
    )

    const webPurchaseEnabled = computed(() => {
      if (isPurchased.value) return true

      return (
        !gameUSDMarketplace.value ||
        mod.value.skus?.some((p) => p.portal === 'web')
      )
    })

    const modMonetized = computed(
      () =>
        isMonetizationEnabled(game.value.monetization_options) &&
        isMonetizationEnabled(mod.value.monetization_options) &&
        (isTokenMarketplaceEnabled(game.value.monetization_options) ||
          gameUSDMarketplace.value) &&
        isMarketplaceEnabledForMod(mod.value.monetization_options)
    )

    const cantPurchase = computed(
      () => paymentRequired.value && !modMonetized.value
    )

    const scarcity = computed(
      () =>
        isScarcityEnabled(game.value.monetization_options) &&
        isScarcityEnabled(mod.value.monetization_options)
    )

    const outOfStock = computed(
      () => paymentRequired.value && scarcity.value && !mod.value.stock
    )

    const subscribeText = computed(() => {
      if (paymentRequired.value) {
        if (outOfStock.value) return t('marketplace.sold_out')

        return gameUSDMarketplace.value
          ? humanPriceFormat(mod.value.price_dollars)
          : `${mod.value.price.toLocaleString()} ${game.value?.token_name || tokenName().value}`
      }
      return isSubscribed.value ? t('subscribed') : t('subscribe')
    })

    const buttonDisabled = computed(
      () =>
        disabled.value ||
        cantPurchase.value ||
        (!webPurchaseEnabled.value && modMonetized.value) ||
        outOfStock.value
    )

    watch(
      isLoggedIn,
      () => {
        if (
          (['1', 'true'].includes(routeQuery.value.purchase) ||
            purchaseMod.value?.id === mod.value.id) &&
          paymentRequired.value
        ) {
          handleClick()
        }
      },
      {
        immediate: true,
      }
    )

    async function handleClick() {
      if (buttonDisabled.value) return

      if (!isLoggedIn.value) {
        setGlobalModal({ modal: MODAL_LOGIN })
        return
      }

      if (paymentRequired.value) {
        if (!isMonRegistered.value) {
          await registerUser()
        }

        showPurchaseModal()
      } else {
        handleSubscribe()
      }
    }

    // We persist subscription state before making request
    // if request fails we revert the changes
    async function handleSubscribe() {
      if (!isSubscribed.value) {
        // use game and mod id
        addSubscription(mod.value.id, mod.value)
        addActivation(mod.value.id, false)
        if (mod.value.dependencies) {
          fetchDependencies()
        }

        await subscribeToMod()

        if (!subscribeError.value) {
          sdkInterface.notifySubscriptionChange(mod.value.id, true)
          sdkInterface.notifyActivationChange(mod.value.id, true)
        } else if (subErrorRef.value !== RESOURCE_ALREADY_SUBSCRIBED) {
          removeSubscription(mod.value.id)
          addToast({
            title: t('subscribe_fail'),
            isError: true,
            text: subscribeError.value,
          })
        }
      } else {
        // use game and mod id
        removeSubscription(mod.value.id)

        await unsubscribeToMod()

        if (!unsubscribeError.value) {
          sdkInterface.notifySubscriptionChange(mod.value.id, false)
        } else if (unsubErrorRef.value !== RESOURCE_NOT_SUBSCRIBED) {
          addSubscription(mod.value.id, mod.value)
          addToast({
            title: t('unsubscribe_fail'),
            isError: true,
            text: unsubscribeError.value,
          })
        }
      }
    }

    async function fetchDependencies() {
      if (dependencies.value) {
        dataDeps.value = { data: dependencies.value }
      } else {
        await runFetchDeps()
      }

      if (!errorDeps.value) {
        const subbedToAll = dataDeps.value.data.every((d) =>
          subscriptions.value.some((s) => d.mod_id === s.id)
        )
        if (!subbedToAll) {
          setGlobalModal({
            modal: MODAL_DEPENDENCIES,
            data: {
              modNameId: mod.value.name_id,
              dependencies: dataDeps.value.data,
            },
          })
        }
      }
    }

    function handlePurchase() {
      if (scarcity.value && mod.value.stock) {
        stock.value -= 1
      }
    }

    function closePurchaseModal() {
      purchaseMod.value = null
      hidePurchaseModal()
    }

    return {
      isPurchaseModalOpen,
      gameUSDMarketplace,
      closePurchaseModal,
      humanNumberFormat,
      buttonDisabled,
      handlePurchase,
      subscribeText,
      isSubscribed,
      handleClick,
      scarcity,
      status,
      stock,
    }
  },
}
</script>
